<template>
  <div
    v-if="!loaded"
    class="text-center"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <LoadingIcon />
  </div>
  <div
    v-else
    v-editable="blok"
    class="FindRetailerBlock"
    :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div class="relative w-full h-auto desk:h-[calc(100dvh-160px)] max-h-auto desk:max-h-[990px] min-h-auto desk:min-h-[560px] bg-bg-secondary">
      <!-- Search & Filter Wrapper -->
      <div
        class="
        ListWrapper w-full max-w-auto desk:max-w-360 max-h-[calc(100%-48px)] overflow-y-auto
        p-16 mobOnly:pt-32 desk:p-24 relative desk:absolute top-0 desk:top-24 left-0 desk:left-24 z-10
        flex flex-col items-stretch gap-24
        bg-bg-subtle outline outline-0 desk:outline-1 outline-bg-inverse/10 shadow-none desk:shadow-lg"
      >
        <div class="flex flex-col items-stretch gap-12 desk:gap-16">
          <!-- Headline -->
          <div class="type-headline-lg">
            {{ blok.name }}
          </div>

          <!-- Search and Filter -->
          <div class="w-full flex gap-8">
            <TextInput
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              :is-search="true"
              :placeholder="$t('findRetailer.searchPlaceholder')"
              class="w-full"
              input-class="placeholder:text-12"
            />
            <button
              class="btn btn--lg min-w-48 gap-8"
              @click="openFilterModal"
            >
              <img
                src="/icons/filter-inv.svg"
                class="w-20 h-20"
                alt=""
              >
              <span v-if="activeFilterCount > 0"> {{ activeFilterCount }} </span>
            </button>
          </div>

          <!-- Use my location -->
          <button
            class="inline-flex items-center gap-8 type-headline-base underline underline-offset-4 decoration-1"
            @click="useCurrentLocation"
          >
            <img
              src="/icons/location.svg"
              class="w-20 h-20"
              alt=""
            >
            {{ $t('findRetailer.currentLocation') }}
          </button>
        </div>

        <!-- Retailer List Desktop -->
        <div v-if="hasInteracted && !isMobile">
          <div class="type-sm mb-16 empty:hidden">{{ showNear }}</div>
          <div v-if="filteredStores.length" class="w-full flex flex-col items-stretch gap-8">
            <FindRetailerStore
              v-for="store in visibleStores"
              :key="store.id"
              :store="store"
              :expanded="expandedStoreId === store.id"
              :display-map="false"
              :info-window="false"
              @select="id => { openStore(id); zoomToStore(visibleStores.find(s => s.id === id)!) }"
              @close="closeDetails"
            />
          </div>
          <div
            v-else-if="!extendingSearch"
            class="w-full flex flex-col items-center justify-center p-24 text-center border border-dashed border-border-base"
          >
            <p class="type-headline-sm mb-4">{{ $t('findRetailer.noResults.title') }}</p>
            <p class="type-sm text-text-subtle">{{ $t('findRetailer.noResults.message') }}</p>
            <button class="btn btn--secondary mt-16" @click="clearFilter">
              {{ $t('findRetailer.noResults.clearFilters') }}
            </button>
          </div>
          <LoadingIcon v-else />
        </div>
      </div>

      <!-- Tabs -->
      <div v-if="isMobile" class="flex bg-bg-subtle type-headline-base">
        <button
          class="flex grow items-center justify-center p-12"
          :class="{
            'border-border-inverse border-b-2': currentTab === 'map',
            'border-border-base border-b text-text-inactive': currentTab !== 'map'
          }"
          @click="currentTab = 'map'"
        >
          {{ $t('findRetailer.tabs.mapView') }}
        </button>
        <button
          class="flex grow items-center justify-center p-12 border-b-2"
          :class="{
            'border-border-inverse border-b-2': currentTab === 'list',
            'border-border-base border-b text-text-inactive': currentTab !== 'list'
          }"
          @click="currentTab = 'list'"
        >
          {{ $t('findRetailer.tabs.listView') }}
        </button>
      </div>

      <!-- Map Wrapper -->
      <div v-if="currentTab === 'map'" class="MapWrapper mobOnly:h-[480px] flex items-center justify-center w-full h-full">
        <GoogleMap
          ref="mapRef"
          :api-key="runtimeConfig.public.googleMapsApiKey"
          style="width: 100%; height: 100%"
          :styles="mapStyles"
          :center="center"
          :zoom="6"
          :disable-default-ui="true"
          @idle="onMapIdle"
        >
          <LoadingIcon v-if="!mapReady" class="absolute top-half left-half -translate-x-half -translate-y-half" />
          <MarkerCluster v-else :options="clusterOptions">
            <Marker
              v-for="store in filteredStores"
              :key="store.id"
              :options="{
                position: { lat: store.latitude, lng: store.longitude },
                icon: customIcon,
              }"
            >
              <InfoWindow>
                <div id="content">
                  <div id="siteNotice" />
                  <div id="bodyContent">
                    <FindRetailerStore
                      :key="store.id"
                      :store="store"
                      :expanded="expandedStoreId === store.id"
                      :display-map="false"
                      :info-window="true"
                      @select="id => { openStore(id); }"
                      @close="closeDetails"
                    />
                  </div>
                </div>
              </InfoWindow>
            </Marker>
          </MarkerCluster>
        </GoogleMap>
      </div>

      <!-- Retailer List Mobile -->
      <div v-if="currentTab === 'list' && isMobile" class="p-16 bg-bg-subtle">
        <div v-if="filteredStores.length" class="w-full flex flex-col items-stretch gap-8">
          <FindRetailerStore
            v-for="store in paginatedStores"
            :key="store.id"
            :store="store"
            :expanded="expandedStoreId === store.id"
            :display-map="true"
            :info-window="false"
            @select="id => { openStore(id); zoomToStore(paginatedStores.find(s => s.id === id)!) }"
            @close="closeDetails"
          />
        </div>

        <!-- LoadProgress Component -->
        <LoadProgress
          v-if="filteredStores.length"
          :current="paginatedStores.length"
          :total="filteredStores.length"
          text="findRetailer.load.quantityText"
          button-text="findRetailer.load.showMoreCta"
          class="py-32"
          @load-more="loadMore"
        />

        <!-- No Results Message -->
        <div
          v-if="filteredStores.length < 1"
          class="w-full flex flex-col items-center justify-center p-24 text-center border border-dashed border-border-base"
        >
          <p class="type-headline-sm mb-4">{{ $t('findRetailer.noResults.title') }}</p>
          <p class="type-sm text-text-subtle">{{ $t('findRetailer.noResults.message') }}</p>
          <button class="btn btn--secondary mt-16" @click="clearFilter">
            {{ $t('findRetailer.noResults.clearFilters') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Filter Modal -->
    <Teleport to="body">
      <transition name="fromLeft">
        <GenericSideMenu
          v-if="showFilterModal"
          position="left"
          :title="$t('findRetailer.filter.title')"
          :self-contained="true"
          @close="closeFilterModal"
        >
          <div class="divide-y divide-border-subtle">
            <!-- Sort order -->
            <div class="pb-16">
              <fieldset>
                <legend class="type-headline-sm mb-12">{{ $t('findRetailer.filter.sort') }}</legend>
                <ul class="flex flex-col items-start justify-start gap-12">
                  <li>
                    <label class="flex items-center justify-start gap-8">
                      <input
                        v-model="sortOption"
                        type="radio"
                        name="sort"
                        value="distance"
                      >
                      <span class="type-sm cursor-pointer">{{ $t('findRetailer.filter.distance') }}</span>
                    </label>
                  </li>
                  <li v-if="!globalContent.disableRetailerExhibitionDetails">
                    <label class="flex items-center justify-start gap-8">
                      <input
                        v-model="sortOption"
                        type="radio"
                        name="sort"
                        value="exhibitionSize"
                      >
                      <span class="type-sm cursor-pointer">{{ $t('findRetailer.filter.exhibitionSize') }}</span>
                    </label>
                  </li>
                </ul>
              </fieldset>
            </div>

            <!-- Exibition type filter -->
            <div v-if="!globalContent.disableRetailerExhibitionDetails" class="py-16">
              <fieldset>
                <legend class="type-headline-sm mb-12">{{ $t('findRetailer.filter.exhibition') }}</legend>
                <ul class="flex flex-col gap-12">
                  <li
                    v-for="ex in exhibitions"
                    :key="ex.name"
                  >
                    <label class="flex items-center gap-8">
                      <input
                        v-model="selectedExhibitions"
                        type="checkbox"
                        :value="ex.name"
                        class="form-checkbox"
                      >
                      <span class="type-sm cursor-pointer">{{ ex.name }}</span>
                    </label>
                  </li>
                </ul>
              </fieldset>
            </div>

            <!-- Floor Types filter -->
            <div class="py-16">
              <fieldset>
                <legend class="type-headline-sm mb-12">{{ $t('findRetailer.filter.floorTypes') }}</legend>
                <ul class="flex flex-col gap-12">
                  <li v-for="floor in floorTypes" :key="floor">
                    <label class="flex items-center gap-8">
                      <input
                        v-model="selectedFloors"
                        type="checkbox"
                        class="form-checkbox"
                        :value="floor"
                      >
                      <span class="type-sm cursor-pointer">{{ floor }}</span>
                    </label>
                  </li>
                </ul>
              </fieldset>
            </div>

            <!-- Exhibition info list -->
            <div v-if="!globalContent.disableRetailerExhibitionDetails" class="py-16">
              <ul class="flex flex-col gap-16">
                <li
                  v-for="ex in exhibitions"
                  :key="ex.name"
                  class="flex flex-col"
                >
                  <div class="flex items-center gap-4 mb-6">
                    <div
                      v-for="n in MAX_RATING"
                      :key="n"
                      class="w-6 h-6 rounded-full"
                      :class="n <= ex.rating
                        ? 'bg-bg-brandStrong'
                        : 'bg-bg-base border border-border-base'"
                    />
                  </div>
                  <div class="type-headline-sm"> {{ ex.name }} </div>
                  <div class="type-sm text-text-subtle"> {{ ex.description }}</div>
                </li>
              </ul>
            </div>
          </div>

          <!-- Apply/Clear Filter -->
          <div class="fixed left-0 bottom-0 w-full p-16 desk:p-24 flex gap-12 border-t border-border-subtle">
            <button
              class="btn btn--secondary"
              @click="clearFilter"
            >
              {{ $t('findRetailer.filter.clearFilter') }}
            </button>
            <button
              class="btn btn--primary grow"
              @click="closeFilterModal"
            >
              {{ $t('findRetailer.filter.applyFilter', {num: filteredStores.length}) }}
            </button>
          </div>
        </GenericSideMenu>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import { StoryBlokPropBase } from '~/constants/types/storyblok';
import { GoogleMap, InfoWindow, Marker, MarkerCluster } from 'vue3-google-map';
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import type { MarkerClustererOptions } from '@googlemaps/markerclusterer';
import FindRetailerStore from '~/components/find-retailer/FindRetailerStore.vue';
import { storeToRefs } from 'pinia';
import { useUiStore } from '~/store/ui';
import TextInput from '~/components/form-elements/TextInput.vue';
import LoadProgress from '~/components/generic/LoadProgress.vue';
import useApiFetch from '~/composeables/useApiFetch';
import { RetailersApiResponse, StoreModel, GeocodeApiResponse } from '~/constants/types/norce';
import { useGlobalContentStore } from '~/store/globalContent';

type Props = StoryBlokPropBase & { name: string, initialListView: boolean, latitude?: number, longitude?: number, center_type?: string };
const props = defineProps<{ blok: Props }>();
const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);
const { apiGet, handleApiError } = useApiFetch();
const { $t } = useNuxtApp();
const route = useRoute();
const runtimeConfig = useRuntimeConfig();
const globalContent = useGlobalContentStore();

// Turn of Points of Interest
const mapStyles = [
  {
    'featureType': 'poi',
    'stylers': [
      {
        'visibility': 'off',
      },
    ],
  },
];

// ——————————————————————————
// UI State
// ——————————————————————————
const uiStore = useUiStore();
const { isMobile } = storeToRefs(uiStore);
const currentTab = ref<'map' | 'list'>(props.blok.initialListView && isMobile.value ? 'list' : 'map');

const showFilterModal = ref(false);
function openFilterModal() { showFilterModal.value = true; }
function closeFilterModal() { showFilterModal.value = false; }

const hasInteracted = ref(false);
let mapInitialIdleSkipped = false;

// ——————————————————————————
// Map
// ——————————————————————————
const center = computed(() => {
  // If center_type is 'center' and we have stores, calculate center from all stores
  if (props.blok.center_type === 'center' && stores.value.length > 0) {
    const lats = stores.value.map(s => s.latitude);
    const lngs = stores.value.map(s => s.longitude);
    const centerLat = lats.reduce((sum, lat) => sum + lat, 0) / lats.length;
    const centerLng = lngs.reduce((sum, lng) => sum + lng, 0) / lngs.length;
    return { lat: centerLat, lng: centerLng };
  }

  // Otherwise use manual coordinates or default
  return {
    lat: Number(props.blok.latitude) || 53.46627192741455,
    lng: Number(props.blok.longitude) || -1.8334145991382833,
  };
});
const mapRef = ref<InstanceType<typeof GoogleMap> | null>(null);
const currentBounds = ref<google.maps.LatLngBounds | null>(null);
const mapReady = ref(false);

const clusterOptions: MarkerClustererOptions = {
  renderer: {
    render({ count, position }) {
      const diameter = 40;

      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg"
             width="${diameter}" height="${diameter}">
          <circle
            cx="${diameter/2}" cy="${diameter/2}" r="${(diameter/2) - 2}"
            fill="#A8003B"/>
          <text
            x="50%" y="52%"
            text-anchor="middle" dominant-baseline="middle"
            fill="#FFFFFF"
            font-family="Arial, sans-serif"
            font-size="14px"
          >${count}</text>
        </svg>`;

      const icon: google.maps.Icon = {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new google.maps.Size(diameter, diameter),
        anchor:     new google.maps.Point(diameter/2, diameter/2),
      };

      return new google.maps.Marker({
        position,
        icon,
        zIndex: google.maps.Marker.MAX_ZINDEX + count,
      });
    },
  },
};

const customIcon = computed<google.maps.Icon | undefined>(() => {
  // bail out during SSR or before the maps API has loaded
  if (typeof window === 'undefined' || !window.google) return;
  return {
    url: '/logos/kahrs-logo-round.svg',
    scaledSize: new google.maps.Size(32, 32),
    anchor:     new google.maps.Point(16, 32),
  };
});

watch(
  () => ({
    ready: mapRef.value?.ready,
  }),
  ({ ready }) => {
    if (ready) mapReady.value = true;
  }
);

function onMapIdle() {
  const map = mapRef.value?.map;
  if (!map) return;
  // update bounds (or null if not ready)
  currentBounds.value = map.getBounds() ?? null;

  // skip the very first fire
  if (!mapInitialIdleSkipped) {
    mapInitialIdleSkipped = true;
  } else {
    hasInteracted.value = true;
  }
}

// ——————————————————————————
// Search / Filters / Sort State
// ——————————————————————————
const searchQuery = ref('');
const selectedExhibitions = ref<string[]>([]);
const selectedFloors = ref<string[]>([]);
const sortOption = ref<'distance' | 'exhibitionSize'>('distance');

// mark interaction if they type or toggle any filter
watch(
  [searchQuery, selectedExhibitions, selectedFloors, sortOption],
  () => { hasInteracted.value = true; }
);

watch (searchQuery, (newVal) => {
  doSearch();
  if (newVal === '') {
    history.replaceState(
      {},
      '',
      route.path
    );
  } else {
    history.replaceState(
      {},
      '',
      route.path + '?search=' + encodeURI(newVal)
    );
  }
});

// ——————————————————————————
// Pagination state
// ——————————————————————————
const visibleCount = ref(2);  // Starts with 2 stores shown
const storesPerPage = 2;  // Number of stores to show per page (you can adjust this if needed)

// Update the visible stores based on pagination
const paginatedStores = computed(() => filteredStores.value.slice(0, visibleCount.value));

function loadMore() {
  // Increase visible count by the number of stores per page (2)
  visibleCount.value += storesPerPage;
}

// ——————————————————————————
// Data Models
// ——————————————————————————

const stores = ref<StoreModel[]>([]);

interface Exhibition {
  name: string;
  rating: number; // 1–4
  description: string;
}

const exhibitions = ref<Exhibition[]>([
  {
    name: $t('findRetailer.rating4.name'),
    rating: 4,
    description: $t('findRetailer.rating4.description'),
  },
  {
    name: $t('findRetailer.rating3.name'),
    rating: 3,
    description: $t('findRetailer.rating3.description'),
  },
  {
    name: $t('findRetailer.rating2.name'),
    rating: 2,
    description: $t('findRetailer.rating2.description'),
  },
]);

function getExhibitionDetails(storeExhibition: string): Exhibition | undefined {
  return exhibitions.value.find((e) => e.name === storeExhibition);
}

const MAX_RATING = 4;

// ——————————————————————————
// Helpers: Distance & Geolocation
// ——————————————————————————
const userLocation = ref<{ lat: number; lng: number } | null>(null);

function toRad(deg: number) { return (deg * Math.PI) / 180; }
function getDistanceKm(
  from: { lat: number; lng: number },
  to:   { lat: number; lng: number }
) {
  const R = 6371;
  const dLat = toRad(to.lat - from.lat);
  const dLon = toRad(to.lng - from.lng);
  const a =
      Math.sin(dLat/2)**2 +
      Math.cos(toRad(from.lat)) *
      Math.cos(toRad(to.lat)) *
      Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const pauseBounds = ref(false);

function useCurrentLocation() {
  if (!navigator.geolocation) {
    alert('Geolocation not supported');
    return;
  }
  pauseBounds.value = true;
  searchQuery.value = '';
  navigator.geolocation.getCurrentPosition(
    ({ coords }) => {
      const loc = { lat: coords.latitude, lng: coords.longitude };
      userLocation.value = loc;

      const map = mapRef.value?.map;
      if (map) {
        map.panTo(loc);

        setTimeout(() => {
          if (mapRef.value?.ready) {
            map.setZoom(12);
          }
        }, 200);
        setTimeout(()=> pauseBounds.value = false, 500);
      }
    },
    () => alert('Unable to retrieve your location')
  );
}

// ——————————————————————————
// Computed: floor types, filtered + sorted, visible in viewport
// ——————————————————————————
const floorTypes = computed(() => {
  const output = [] as string[];
  stores.value.forEach(s =>
    s.floors.split(',').forEach(f => {
      if (!output.includes(f) && f !== '') {
        output.push(f);
      }
    })
  );
  return output;
});

const filteredStores = ref<StoreModel[]>([]);
const extendingSearch = ref(false);
const showNear = ref('');

const timer = ref<ReturnType<typeof setTimeout> | undefined>(undefined);

const doExtendedSearch = async() => {
  const term = searchQuery.value.trim().toLowerCase();

  // To test this function you need to be connected to iveo vpn
  const positionLookup = await apiGet<GeocodeApiResponse>(`/geocode?address=${term}&country=${globalContent.getCountryCode}`);

  if (positionLookup && positionLookup.lat && positionLookup.lng) {
    const city = positionLookup.formatted_address.split(' ')[0];

    filteredStores.value = stores.value.filter((store) => {
      const dist = getDistanceKm(
        { lat: positionLookup.lat, lng: positionLookup.lng },
        { lat: store.latitude, lng: store.longitude }
      );
      return dist < 20;
    });

    showNear.value = $t('findRetailer.near', { city });
  }

  extendingSearch.value = false;
};

const doSearch = () => {
  clearTimeout(timer.value);
  showNear.value = '';

  const term = searchQuery.value.trim().toLowerCase();
  const loc  = userLocation.value;

  // compute distance
  const withDist = stores.value.map(store => {
    if (loc) {
      store.distance = getDistanceKm(loc, { lat: store.latitude, lng: store.longitude });
    }
    return store;
  });

  // exact match
  const exactMatch = stores.value.filter(f => f.city.toLowerCase() === term);

  let results = exactMatch.length ? exactMatch : [] as StoreModel[];

  // filter
  if (!exactMatch.length) {
    results = withDist.filter(store => {
      if (term) {
        const nameMatch = store.name.toLowerCase().includes(term);
        const cityMatch = store.city.toLowerCase().includes(term);
        const addrMatch = store.addressLine1.toLowerCase().includes(term);
        if (!nameMatch && !cityMatch && !addrMatch) {
          return false;
        }
      }
      if (
        selectedExhibitions.value.length &&
        !selectedExhibitions.value.includes(store.exhibition)
      ) {
        return false;
      }
      if (selectedFloors.value.length) {
        const arr = store.floors.split(',').map(f => f.trim());
        if (!selectedFloors.value.some(f => arr.includes(f))) {
          return false;
        }
      }
      return true;
    });
  }

  if (!results.length && term) {
    extendingSearch.value = true;
    timer.value = setTimeout(()=> doExtendedSearch(), 500);
  }

  // sort
  if (sortOption.value === 'distance') {
    results.sort((a,b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
  }
  else if (sortOption.value === 'exhibitionSize') {
    results.sort((a,b) => {
      const ra = getExhibitionDetails(a.exhibition)?.rating ?? 0;
      const rb = getExhibitionDetails(b.exhibition)?.rating ?? 0;
      return rb - ra;
    });
  }

  return filteredStores.value = results;
};

// only those in viewport (after first idle)
const visibleStores = computed(() => {
  if (!currentBounds.value) return filteredStores.value;
  return filteredStores.value.filter(s =>
      currentBounds.value!.contains({ lat: s.latitude, lng: s.longitude })
  );
});

// ——————————————————————————
// Side‐effects: auto‐fit bounds on filter change
// ——————————————————————————
watch(
  filteredStores,
  (stores) => {
    if (!pauseBounds.value) {
      const map = mapRef.value?.map;
      const api = mapRef.value?.api;
      if (!map || !api || !stores.length) {
        return;
      }

      // Only auto-fit bounds if center_type is 'center', otherwise use manual center
      if (props.blok.center_type === 'center') {
        const bounds = new api.LatLngBounds();
        stores.forEach(s => bounds.extend({ lat: s.latitude, lng: s.longitude }));
        map.fitBounds(bounds);
        if (stores.length === 1) {
          map.setZoom(12);
        }
      }
    }
  },
  { immediate: true }
);

// ——————————————————————————
// Other helpers
// ——————————————————————————
function clearFilter() {
  searchQuery.value = '';
  selectedExhibitions.value = [];
  selectedFloors.value = [];
}

function zoomToStore(store: StoreModel) {
  const map = mapRef.value?.map;
  if (!map) return;
  map.panTo({ lat: store.latitude, lng: store.longitude });
  map.setZoom(12);
}

// Count active filters
const activeFilterCount = computed(
  () => selectedExhibitions.value.length + selectedFloors.value.length
);

// track which store is expanded
const expandedStoreId = ref<string|null>(null);

// open store
function openStore(storeId: string) {
  expandedStoreId.value = storeId;
}

// closes the details and resets the map zoom
function closeDetails() {
  // 1) collapse the panel
  expandedStoreId.value = null;

  // 2) zoom out to show all filtered stores
  const map = mapRef.value?.map;
  const api = mapRef.value?.api;
  if (map && api && filteredStores.value.length) {
    const bounds = new api.LatLngBounds();
    filteredStores.value.forEach(s =>
      bounds.extend({ lat: s.latitude, lng: s.longitude })
    );
    map.fitBounds(bounds);
    map.setZoom(12);
  }
  // fallback: go back to default center/zoom
  else if (map) {
    map.panTo(center.value);
    map.setZoom(6);
  }
}

// ——————————————————————————
// Load data
// ——————————————————————————

const loaded = ref(false);

const loadRetailers = async() => {
  const res = await apiGet<RetailersApiResponse>('/retailers');
  if (res) {
    stores.value = res.stores.map((store, index) => {
      let floors = [] as string[];
      if (store.wood) {
        floors.push($t('findRetailer.kind.wood'));
      }
      if (store.resilient) {
        floors.push($t('findRetailer.kind.resilient'));
      }

      return {
        ...store,
        id: store.name + index,
        floors: floors.join(','),
        exhibition: $t(`findRetailer.rating${store.exibitionRating}.name`),
        address: store.addressLine1 + ' ' + store.addressLine2,
      };
    }).filter((f) => {
      // wrong data from backend
      return f.longitude && f.latitude && f.showonweb;
    });

    loaded.value = true;

    doSearch();

    if (route.query.search && typeof route.query.search === 'string') {
      setTimeout(()=> {
        searchQuery.value = route.query.search as string;
        doSearch();
      }, 250);
    }

  } else {
    handleApiError();
  }
};

onMounted(()=> {
  loadRetailers();
});

</script>

<style scoped lang="postcss">

</style>
